import { Filter, X, ChevronDown } from 'lucide-react';
import { FilterField } from './data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';

export interface InlineFilterProps {
  showFilters: boolean;
  onToggle: () => void;
  filterFields: FilterField[];
  activeFilters: { [key: string]: string };
  onFilterChange: (filters: { [key: string]: string }) => void;
  onFiltersClear: () => void;
  onFiltersApply: (filters: { [key: string]: string }) => void;
  title?: string;
  className?: string;
}

export const InlineFilter = ({
  showFilters,
  filterFields,
  activeFilters,
  onFilterChange,
  title = 'Filters',
  className = '',
}: InlineFilterProps) => {
  const handleFilterValueChange = (fieldId: string, value: string) => {
    const newFilters = {
      ...activeFilters,
      [fieldId]: value,
    };
    onFilterChange(newFilters);
  };

  const handleRemoveFilter = (key: string) => {
    const newFilters = { ...activeFilters };
    delete newFilters[key];
    onFilterChange(newFilters);
  };

  if (!showFilters) return null;

  return (
    <div
      className={`mb-6 p-4 border border-blue-200 rounded-lg bg-blue-50/50 ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-blue-600" />
          <h3 className="text-sm font-semibold text-blue-900">{title}</h3>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 items-end">
        {filterFields.map(field => {
          const selectedOption = field.options.find(
            option => option.value === activeFilters[field.id]
          );
          const displayValue = selectedOption
            ? selectedOption.label
            : `All ${field.label}`;

          return (
            <div key={field.id} className="flex flex-col gap-1 min-w-[150px]">
              <label className="text-xs font-medium text-gray-700">
                {field.label}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm flex items-center justify-between hover:bg-gray-50 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    type="button"
                  >
                    <span className="truncate">{displayValue}</span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="min-w-[150px] z-50"
                  side="bottom"
                  align="start"
                >
                  <DropdownMenuItem
                    onClick={() => handleFilterValueChange(field.id, '')}
                    className={`cursor-pointer ${!activeFilters[field.id] ? 'bg-accent' : ''}`}
                  >
                    All {field.label}
                  </DropdownMenuItem>
                  {field.options.map(option => (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() =>
                        handleFilterValueChange(field.id, option.value)
                      }
                      className={`cursor-pointer ${
                        activeFilters[field.id] === option.value
                          ? 'bg-accent'
                          : ''
                      }`}
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        })}
      </div>

      {Object.keys(activeFilters).length > 0 && (
        <div className="mt-4 pt-4 border-t border-blue-200">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-medium text-gray-600">
              Active filters:
            </span>
            {Object.entries(activeFilters).map(([key, value]) => {
              const field = filterFields.find(f => f.id === key);
              const option = field?.options.find(o => o.value === value);
              return option ? (
                <span
                  key={key}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                >
                  {field?.label}: {option.label}
                  <button
                    onClick={() => handleRemoveFilter(key)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ) : null;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default InlineFilter;
